import qs from 'qs'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useSignupContext } from 'user/signup/context/signup-context'
import { signupService } from 'user/signup/services'
import { hasUnknownError } from 'user/signup/utils'
import { loginPostMessage } from 'user/signup/utils/appPostMessage'
import { handleErrorsMessage, isServerError } from 'user/utils'
import * as dataLayer from 'user/signup/utils/data-layer'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { storesApi } from 'api'

import { useUserResources } from 'shared/hooks'

import { tracker } from 'utils/app'

import { FORM_DEFAULT_VALUES, FORM_FIELDS_CODES } from './constants'
import useNewsLetterOptin, { UserInfo } from './useNewsLetterOptin'

type Store = {
  code: number,
  id: string
  name: string
}

type QueryParams = qs.ParsedQs & { store: string }

type RequestError = {
  data: {
    errors: {
      code: 'fiscalId'
        | 'name'
        | 'email'
        | 'emailConfirm'
        | 'phone'
        | 'password'
        | 'loyalty'
        | 'loyalty.optInPro'
        | 'loyalty.favoriteStore'
        | 'loyalty.birthdate'
        | 'loyalty.occupation',
      message: string,
      messages: {
        code: string
        message: string
      }[]
    }[]
  },
  status: number
}

export function useFormPF () {
  const {
    clearErrors,
    control,
    register,
    formState,
    setError,
    setValue,
    getValues,
    handleSubmit,
    watch,
    trigger
  } = useForm({
    mode: 'onBlur',
    defaultValues: FORM_DEFAULT_VALUES
  })

  const { executeRecaptcha } = useGoogleReCaptcha()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [classOccupation, setClassOccupation] = useState('')
  const [stores, setStores] = useState<Store[]>([])

  const history = useHistory()
  const redirectTo = useRef()
  const { setIsLoggedIn, isCaptchaEnabled } = useSignupContext()
  const { userResources, userControls } = useUserResources()

  const { generalOptin, handleOptinsToggle, handleGeneralOptinToggle } =
    useNewsLetterOptin(setValue, getValues)

  const isProfessionalWatch = watch('loyalty.optInPro')
  const passwordWatch = watch('password')

  const queryParams = qs.parse(history.location.search, {
    ignoreQueryPrefix: true
  }) as QueryParams

  const getSignupParams = async (value: UserInfo) => {
    const formData = {
      ...value,
      phone: `+55${value.phone}`
    }

    if (!executeRecaptcha) {
      return null
    }

    const recaptchaToken = isCaptchaEnabled
      ? await executeRecaptcha('signUpFormPF')
      : null

    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content

    return {
      data: formData,
      redirectTo: queryParams.redirect || '/',
      recaptchaToken,
      csrfToken
    }
  }

  const onFormSubmit = async (value: UserInfo) => {
    setIsSubmitting(true)

    const signupParams = await getSignupParams(value)

    try {
      const { data } = await signupService.signup(signupParams)

      redirectTo.current = data.redirect

      if (userControls?.isOnMobileApp) {
        loginPostMessage(data.token)
      }

      if (data.needDocumentation) {
        setClassOccupation(signupParams.data.loyalty?.occupation)
        return
      }

      dataLayerSubmitFormSuccess()
      onSignupCompleted()
    } catch (error) {
      const { data, status } = error as RequestError

      const hasServerError = status && isServerError(status)
      const hasSomeUnknowError =
        errors && hasUnknownError(data.errors, FORM_FIELDS_CODES)

      if (hasServerError || hasSomeUnknowError) {
        history.push('/erro-interno')
      }

      const dataLayerErrors: any = {}
      if (data.errors && Array.isArray(data.errors)) {
        for (const { code, message, messages } of data.errors) {
          setError(code, {
            type: 'manual',
            message: handleErrorsMessage(message, messages)
          })
          dataLayerErrors[code] = ''
        }
      }
      onFormInvalid(dataLayerErrors)
    } finally {
      setIsSubmitting(false)
    }
  }

  const dataLayerSubmitFormSuccess = () => {
    const dataLayerObj = {
      signupType: 'pf',
      optinsSelected: dataLayer.optinsSelectedFormatter(getValues())
    }

    dataLayer.onSubmitFormSuccess(dataLayerObj)
  }

  const onFormInvalid = async (errors: RequestError) => {
    const dataLayerObj = {
      signupType: 'pf',
      fieldsStatus: dataLayer.fieldsStatusFormatter(errors),
      optinsSelected: dataLayer.optinsSelectedFormatter(getValues())
    }
    dataLayer.onSubmitFormWithFieldsErrors(dataLayerObj)
  }

  const onSignupCompleted = () => {
    tracker.signUp('site-pf')

    setIsLoggedIn(true)

    history.push({
      pathname: '/sucesso',
      state: {
        redirect: redirectTo.current,
        signupType: 'pf'
      }
    })
  }

  useEffect(() => {
    passwordWatch && trigger('password')
  }, [passwordWatch, trigger])

  useEffect(() => {
    if (!userResources?.region) {
      return
    }

    const fetchStores = async (region: string) => {
      try {
        const { data } = await storesApi.getStoresByRegion(region)

        const storesArray = data.map((store: Store) => ({
          value: store.id,
          label: store.name
        }))

        const store = storesArray.find(
          (item: { value: string, label: string }) =>
            item.value === queryParams.store
        )

        if (store) {
          setValue('loyalty.favoriteStore', queryParams.store)
        }

        setStores(storesArray)
      } catch (error) {
        history.push('/erro-interno')
      }
    }

    fetchStores(userResources?.region)
  }, [userResources?.region, history, queryParams.store, setValue])

  const { errors } = formState

  return {
    clearErrors,
    control,
    register,
    setError,
    getValues,
    handleSubmit,
    watch,
    isSubmitting,
    classOccupation,
    stores,
    generalOptin,
    handleOptinsToggle,
    handleGeneralOptinToggle,
    isProfessionalWatch,
    onFormSubmit,
    onFormInvalid,
    onSignupCompleted,
    errors
  }
}
